/**
 * PATH: packages/email-hub/src/feature/reader-header/model/buildReaderHeader.ts
 * FILE_ID: EMAILHUB-READERHEADER-MODEL-BUILDER-001
 * PURPOSE: Build the shared preview/full-view reader-header view model.
 * ROLE IN ARCHITECTURE: Pure adapter boundary from API email state to UI state.
 * MAIN EXPORTS: buildReaderHeaderViewModel.
 * NON-RESPONSIBILITIES: This selector does not own transient menu or mutation state.
 * NOTES FOR FUTURE AI: Both reader hosts must call this builder; host-only derivation creates drift.
 */
import type { Email } from '../../hooks/useEmails'
import { sortReaderNotices } from '../notices/noticeRegistry'
import { buildReaderContext } from './buildReaderContext'
import {
  buildReaderDecisionQueue,
  filterResolvedDecisions,
} from './buildDecisionQueue'
import {
  selectPrimaryPlacement,
  selectReaderPlacements,
} from './placements'
import type {
  ReaderHeaderSurface,
  ReaderHeaderViewModel,
  ReaderNotice,
} from './types'

export interface BuildReaderHeaderInput {
  email: Email
  surface: ReaderHeaderSurface
  shellOrgId?: number | null
  /** Membership count for the cross-organisation decision gate (see queue). */
  memberOrgCount?: number | null
  notices?: readonly ReaderNotice[]
  /**
   * Decision ids the operator resolved THIS session (optimistic advance
   * before the server echo rebuilds the email payload). Unknown ids are
   * ignored, so a stale local id can never hide an unrelated decision.
   */
  resolvedDecisionIds?: readonly string[]
}

/**
 * Build one stable view model for every whole-header host.
 *
 * Inputs: one authoritative email plus host surface/organisation context.
 * Output: serialisable reader-header state. Side effects: none. Missing sender
 * and subject values receive safe display fallbacks.
 */
export function buildReaderHeaderViewModel({
  email,
  surface,
  shellOrgId,
  memberOrgCount,
  notices = [],
  resolvedDecisionIds = [],
}: BuildReaderHeaderInput): ReaderHeaderViewModel {
  const placements = selectReaderPlacements(email)
  const decisions = filterResolvedDecisions(
    buildReaderDecisionQueue({ email, shellOrgId, memberOrgCount }),
    resolvedDecisionIds,
  )

  return {
    surface,
    emailId: email.id,
    subject: email.subject?.trim() || '(no subject)',
    senderName:
      email.from_name?.trim() ||
      email.from_address?.split('@')[0] ||
      'Unknown sender',
    senderAddress: email.from_address || '',
    context: buildReaderContext(email),
    placements,
    primaryPlacement: selectPrimaryPlacement(placements),
    decisions,
    currentDecision: decisions[0] ?? null,
    notices: sortReaderNotices(notices),
  }
}
